import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, VideoCamera, Microphone, MonitorPlay, ChatCircleText } from "@phosphor-icons/react";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";

/**
 * MedexOVA Meet — branded Jitsi-powered meeting room.
 * The Jitsi External API is lazy-loaded on demand right here (removed from
 * the global index.html — it was crashing unrelated public pages like the
 * driver beacon link with an opaque cross-origin "Script error." when
 * Safari's tracking prevention blocked it. Only rooms that actually need
 * Jitsi should ever request it.)
 *
 * Self-host swap: set REACT_APP_JITSI_DOMAIN=meet.medexova.com
 * (JaaS or self-hosted) and this room automatically routes there.
 */
const JITSI_DOMAIN = process.env.REACT_APP_JITSI_DOMAIN || "meet.jit.si";
function waitForJitsi(timeoutMs = 20000) {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.JitsiMeetExternalAPI) return resolve(window.JitsiMeetExternalAPI);
    // Force-load if missing (handles cached pages / blocked async tag)
    if (typeof document !== "undefined" && !document.getElementById("astat-jitsi-api")) {
      const s = document.createElement("script");
      s.id = "astat-jitsi-api";
      s.src = `https://${JITSI_DOMAIN}/external_api.js`;
      s.async = true;
      document.head.appendChild(s);
    }
    const start = Date.now();
    const tick = () => {
      if (typeof window !== "undefined" && window.JitsiMeetExternalAPI) return resolve(window.JitsiMeetExternalAPI);
      if (Date.now() - start > timeoutMs) return reject(new Error("Jitsi API failed to load — please refresh"));
      setTimeout(tick, 150);
    };
    tick();
  });
}

export default function MeetingRoom() {
  const { roomId } = useParams();
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [needName, setNeedName] = useState(true);
  const [overlayDone, setOverlayDone] = useState(false);

  // Fetch public meta
  useEffect(() => {
    let mounted = true;
    axios.get(`${BACKEND_URL}/api/public/meet/${roomId}`)
      .then(({ data }) => { if (mounted) setMeta(data); })
      .catch((e) => { if (mounted) setError(e?.response?.data?.detail || "Meeting not found"); });
    return () => { mounted = false; };
  }, [roomId]);

  // Safety: if `videoConferenceJoined` never fires (e.g. user is still granting cam/mic),
  // dismiss the "Connecting…" overlay after 6 s so the Jitsi UI underneath becomes visible.
  useEffect(() => {
    if (needName || overlayDone) return;
    const t = setTimeout(() => setOverlayDone(true), 6000);
    return () => clearTimeout(t);
  }, [needName, overlayDone]);

  // Boot Jitsi after the user enters their name
  useEffect(() => {
    if (!meta || needName || apiRef.current || !containerRef.current) return;
    let api;
    let canceled = false;
    waitForJitsi()
      .then((Jitsi) => {
        if (canceled) return;
        api = new Jitsi(JITSI_DOMAIN, {
          roomName: meta.room_id,
          parentNode: containerRef.current,
          width: "100%",
          height: "100%",
          userInfo: { displayName },
          configOverwrite: {
            startWithAudioMuted: true,
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            // disableThirdPartyRequests: true blocks Jitsi's own XMPP/BOSH on meet.jit.si — must stay FALSE
            disableThirdPartyRequests: false,
            enableWelcomePage: false,
            defaultLanguage: "en",
          },
          interfaceConfigOverwrite: {
            APP_NAME: "MedexOVA Meet",
            NATIVE_APP_NAME: "MedexOVA Meet",
            PROVIDER_NAME: "MedexOVA",
            JITSI_WATERMARK_LINK: "https://www.medexova.work",
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_BRAND_WATERMARK: false,
            SHOW_POWERED_BY: false,
            DEFAULT_LOGO_URL: "/logo.png",
            DEFAULT_WELCOME_PAGE_LOGO_URL: "/logo.png",
            DISABLE_VIDEO_BACKGROUND: false,
            HIDE_INVITE_MORE_HEADER: true,
            MOBILE_APP_PROMO: false,
            DISABLE_FOCUS_INDICATOR: false,
            TOOLBAR_BUTTONS: [
              "microphone","camera","desktop","chat","raisehand","participants-pane",
              "tileview","fullscreen","fodeviceselection","hangup","settings","videoquality","filmstrip",
            ],
          },
        });
        api.addListener("videoConferenceJoined", () => { setJoined(true); setOverlayDone(true); });
        api.addListener("conferenceJoined", () => { setJoined(true); setOverlayDone(true); });
        api.addListener("readyToClose", () => {
          window.location.href = "/";
        });
        api.addListener("errorOccurred", (err) => {
          // Surface connection failures so users can refresh rather than stare at a blank iframe
          // eslint-disable-next-line no-console
          console.warn("[MedexOVA Meet] Jitsi error", err);
          setOverlayDone(true);
          if (err && (err.error?.isFatal || err.type === "CONNECTION_FAILED")) {
            setError("Lost connection to MedexOVA Meet — please refresh the page.");
          }
        });
        apiRef.current = api;
      })
      .catch((e) => setError(`Could not load video engine: ${e.message}`));
    return () => {
      canceled = true;
      try { api && api.dispose(); } catch (_) { /* ignore */ }
      apiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, needName]);

  if (error) {
    return (
      <main className="min-h-screen bg-brandblue-ink text-white grid place-items-center px-6" data-testid="meeting-error">
        <div className="text-center max-w-sm">
          <img src="/logo.png" alt="" className="h-14 w-14 mx-auto" />
          <h1 className="mt-6 font-heading text-3xl font-black">Meeting unavailable</h1>
          <p className="mt-3 text-white/60 text-sm">{error}</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-brandblue hover:text-white text-sm">
            <ArrowLeft size={14} /> Back to MedexOVA
          </Link>
        </div>
      </main>
    );
  }

  if (!meta) {
    return <main className="min-h-screen bg-brandblue-ink text-white grid place-items-center text-sm text-white/60">Loading…</main>;
  }

  const dt = new Date(meta.scheduled_at);
  const niceWhen = dt.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

  if (needName) {
    return (
      <main className="min-h-screen bg-brandblue-ink text-white grid place-items-center px-4" data-testid="meeting-prejoin">
        <div aria-hidden className="orb h-[520px] w-[520px] bg-brandblue/30 -top-40 -left-40" />
        <div aria-hidden className="orb h-[400px] w-[400px] bg-ova/25 -bottom-20 -right-10" />
        <div aria-hidden className="absolute inset-0 grid-bg opacity-40" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="relative z-10 max-w-md w-full bg-white/[0.04] border border-white/10 rounded-2xl p-7 backdrop-blur">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-xl bg-gradient-to-br from-brandblue to-ova grid place-items-center ring-1 ring-white/20">
              <VideoCamera size={20} weight="fill" />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brandblue">MedexOVA Meet</div>
              <div className="font-heading text-xl font-black leading-none mt-1">{meta.title}</div>
            </div>
          </div>
          <div className="mt-4 text-sm text-white/70">
            {niceWhen} · {meta.duration_minutes} min · with {meta.host_name}
          </div>
          {meta.agenda && (
            <div className="mt-4 rounded-lg bg-white/[0.04] border border-white/10 p-3 text-xs text-white/80 whitespace-pre-wrap">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brandblue mb-1">Agenda</div>
              {meta.agenda}
            </div>
          )}
          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/60">Your name</label>
            <input
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="meeting-name-input"
              className="mt-1 w-full rounded-md bg-white/[0.06] border border-white/15 px-3 py-2.5 text-sm focus:border-brandblue outline-none"
              placeholder="What should we call you?"
              onKeyDown={(e) => e.key === "Enter" && displayName.trim() && setNeedName(false)}
            />
          </div>
          <button
            onClick={() => displayName.trim() && setNeedName(false)}
            disabled={!displayName.trim()}
            data-testid="meeting-join-btn"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-brandblue to-ova hover:from-brandblue-deep hover:to-ova-hover text-white px-5 py-3 text-sm font-bold shadow-[0_10px_30px_-10px_rgba(58,123,213,0.6)] disabled:opacity-50"
          >
            <VideoCamera size={14} weight="fill" /> Enter meeting
          </button>
          <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-[0.16em] font-semibold text-white/50">
            <div className="rounded-md border border-white/10 p-2 text-center"><Microphone size={12} className="mx-auto mb-1" />Audio</div>
            <div className="rounded-md border border-white/10 p-2 text-center"><MonitorPlay size={12} className="mx-auto mb-1" />Screen share</div>
            <div className="rounded-md border border-white/10 p-2 text-center"><ChatCircleText size={12} className="mx-auto mb-1" />Chat</div>
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-white/30 inline-flex items-center gap-1">
            <ShieldCheck size={11} weight="fill" /> End-to-end encrypted media · MedexOVA hosted
          </div>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black relative" data-testid="meeting-room">
      {!joined && !overlayDone && (
        <div className="absolute inset-0 z-10 grid place-items-center bg-brandblue-ink text-white text-sm pointer-events-none">
          <div className="text-center">
            <div className="font-heading text-2xl font-black">Connecting to MedexOVA Meet…</div>
            <p className="mt-2 text-white/60 text-xs">If your browser asks for camera/microphone access, please allow it.</p>
          </div>
        </div>
      )}
      <div ref={containerRef} className="w-full" style={{ height: "100vh" }} data-testid="meeting-iframe-container" />
    </main>
  );
}
